import { useMemo, useState } from 'react';
import { Link2, Copy, Check, ExternalLink, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DEEP_LINK_PAGES, findDeepLinkPage, buildDeepLinkUrl } from '@/lib/deepLinkPages';
import { findEntitySource } from '@/lib/deepLinkEntities';
import { DeepLinkEntityPicker } from '@/components/DeepLinkEntityPicker';

const GROUPS = Array.from(new Set(DEEP_LINK_PAGES.map((p) => p.group)));

export default function LinkGenerator() {
  const [pageKey, setPageKey] = useState<string>('');
  const [idValue, setIdValue] = useState('');
  const [copied, setCopied] = useState(false);

  const page = pageKey ? findDeepLinkPage(pageKey) : undefined;
  // A detail page whose table this tool knows how to search gets the picker;
  // anything else keeps the plain ID field. `idValue` stays the only input to
  // link building either way, so the URL logic below is unchanged.
  const entitySource = page?.requiresId ? findEntitySource(page.key) : undefined;
  const idReady = !page?.requiresId || idValue.trim().length > 0;
  const canGenerate = !!page && idReady;

  const generatedUrl = useMemo(() => {
    if (!page || !idReady) return '';
    return buildDeepLinkUrl(page.key, page.requiresId ? idValue.trim() : undefined);
  }, [page, idReady, idValue]);

  const qrUrl = generatedUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(generatedUrl)}`
    : '';

  const handleCopy = () => {
    if (!generatedUrl) return;
    navigator.clipboard.writeText(generatedUrl).then(() => {
      setCopied(true);
      toast.success('Link copied to clipboard');
      window.setTimeout(() => setCopied(false), 1500);
    }).catch(() => toast.error('Could not copy — try selecting the link manually'));
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* items-start, not items-center: on a phone the description wraps to
          four lines and a centred 40px icon floats in the middle of the block,
          detached from the heading it belongs to. */}
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
          <Link2 className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Deep Link Generator</h1>
          <p className="text-sm text-muted-foreground">
            Build a shareable link to any page in the creator app — for newsletter CTAs, emails, or social.
            Opens the app directly on a phone that has it installed, otherwise falls back to the App/Play Store.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">1. Choose a page</CardTitle>
          <CardDescription>Pick the screen you want the link to open.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label>Page</Label>
            <Select
              value={pageKey}
              onValueChange={(v) => { setPageKey(v); setIdValue(''); }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a page…" />
              </SelectTrigger>
              <SelectContent>
                {GROUPS.map((group) => (
                  <SelectGroup key={group}>
                    <SelectLabel>{group}</SelectLabel>
                    {DEEP_LINK_PAGES.filter((p) => p.group === group).map((p) => (
                      <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>

          {page?.requiresId && (
            <div className="space-y-2">
              <Label htmlFor="deep-link-id">
                {entitySource ? (page.idLabel ?? 'ID').replace(/\s*ID$/i, '') : (page.idLabel ?? 'ID')}
              </Label>
              {entitySource ? (
                <DeepLinkEntityPicker
                  source={entitySource}
                  sourceKey={page.key}
                  value={idValue}
                  onChange={setIdValue}
                  idPlaceholder={page.idPlaceholder}
                />
              ) : (
                <Input
                  id="deep-link-id"
                  value={idValue}
                  onChange={(e) => setIdValue(e.target.value)}
                  placeholder={page.idPlaceholder}
                  autoComplete="off"
                />
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className={canGenerate ? 'border-primary/30' : undefined}>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            2. Your link
          </CardTitle>
          <CardDescription>
            {canGenerate
              ? 'Ready to share — copy it into your newsletter, email, or CTA button.'
              : 'Pick a page above to generate a link.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {canGenerate ? (
            <div className="flex flex-col sm:flex-row gap-6">
              <div className="flex-1 space-y-3">
                <div className="flex items-center gap-2">
                  <Input readOnly value={generatedUrl} className="font-mono text-sm" onFocus={(e) => e.target.select()} />
                  <Button onClick={handleCopy} variant={copied ? 'secondary' : 'default'} className="shrink-0">
                    {copied ? <Check className="h-4 w-4 mr-1.5" /> : <Copy className="h-4 w-4 mr-1.5" />}
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline">{page!.label}</Badge>
                  {page!.requiresId && <Badge variant="outline">ID: {idValue.trim()}</Badge>}
                  <a
                    href={generatedUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors ml-auto"
                  >
                    Preview <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </div>
              {qrUrl && (
                <div className="flex flex-col items-center gap-2 shrink-0">
                  <img src={qrUrl} alt="Deep link QR code" width={112} height={112} className="rounded-md border" />
                  <span className="text-[11px] text-muted-foreground">Scan to test on your phone</span>
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground py-6 text-center border border-dashed rounded-md">
              No link generated yet
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
